import { useState, useCallback, useMemo, useRef } from 'react'
import type { LabelQuery, RangeFilter, DateRange } from '../components/social-media/types'
import {
  EMPTY_SOCIAL_FILTER_STATE,
  buildLabelQuery,
  detectComparisonMode,
  type ComparisonModeInfo,
  type SocialFilterState,
} from '../components/social-media/entityQuery'
import {
  ALL_SOURCES_CHOICE,
  DASHBOARD_INGEST_SCOPE,
  sourcesForQuery,
} from '../components/social-media/businessSource'
import {
  ALL_PLATFORMS,
  ALL_SOURCES,
  choosePlatform,
  initialControlsState,
  resolveControlsForJob,
  platformChoice,
  platformsForQuery,
  saveDashboardControls,
  dashboardControlsKey,
  type AuthorScope,
  type PlatformChoice,
  type PlatformSelection,
  type SourceScope,
} from '../components/social-media/platformFilter'

export interface UseSocialFiltersOptions {
  /** Only used to key the stored controls when there is no product. */
  customer?: string
  job?: string
  /**
   * A Sky product slug. When given it REPLACES customer/job as the storage
   * scope, because all five departments share one customer/job and would
   * otherwise inherit each other's platform de-selections (T2.2).
   */
  product?: string
  /**
   * The mount's real platforms, from the filter summary. The toggles derive
   * from this rather than a hardcoded list, so a new platform appears the day
   * data arrives.
   */
  availablePlatforms?: string[]
  /**
   * False on a product whose INGEST source the server pins (every department,
   * see `sourceIsSelectable`): the client then sends no ingest predicate at
   * all. True on the selectable `all` view, which sends the same pin itself —
   * Sprinklr and the influencer scrapes, never the legacy Apify archive.
   * There is no ingest control any more (21 Oct 2026): the Source row is the
   * BUSINESS source, an entity filter, below.
   */
  sourceSelectable?: boolean
  /**
   * What the Published and Comment date filters open on and `resetDateRanges`
   * returns them to. The Sky dashboards pass "up to the end of yesterday"
   * (`defaultSkyDateRange`); omitted, both start empty as they always did.
   * Read once, on mount: the page computes it from the clock at load.
   */
  defaultDateRange?: DateRange | null
}

export interface UseSocialFiltersResult {
  selectedLabels: Record<string, string[]>
  setSelectedLabels: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
  clearLabels: () => void

  rangeFilters: Record<string, RangeFilter>
  setRangeFilters: React.Dispatch<React.SetStateAction<Record<string, RangeFilter>>>
  clearRangeFilters: () => void

  platforms: string[]
  clearPlatforms: () => void
  platformSelection: PlatformSelection
  setPlatformSelection: (selection: PlatformSelection) => void
  /** The single-select control's value: a platform key, or ALL_PLATFORMS_CHOICE. */
  platformChoice: PlatformChoice
  selectPlatform: (choice: PlatformChoice) => void

  campaigns: string[]
  setCampaigns: React.Dispatch<React.SetStateAction<string[]>>
  clearCampaigns: () => void

  authorScope: AuthorScope
  setAuthorScope: (scope: AuthorScope) => void
  /** The record-level ingest pin; fixed, never chosen (see sourceSelectable). */
  sourceScope: SourceScope

  /** The Source row: one business source, or ALL_SOURCES_CHOICE. */
  sourceChoice: string
  selectSource: (choice: string) => void
  clearSources: () => void

  publishedDateRange: DateRange | null
  setPublishedDateRange: React.Dispatch<React.SetStateAction<DateRange | null>>
  commentDateRange: DateRange | null
  setCommentDateRange: React.Dispatch<React.SetStateAction<DateRange | null>>
  /** Both date filters back to `defaultDateRange` (empty when there is none). */
  resetDateRanges: () => void

  metadataFilters: Record<string, string>
  setMetadataFilters: React.Dispatch<React.SetStateAction<Record<string, string>>>
  clearMetadataFilters: () => void

  /** The flat state every consumer derives from. */
  filterState: SocialFilterState
  /** The wire form: entity-level filters travel inside `entity`. */
  labelQuery: LabelQuery
  /** A stable string for cache keys — `JSON.stringify(labelQuery)`. */
  labelsKey: string
  comparisonInfo: ComparisonModeInfo
}

/**
 * The social dashboards' filter state, owned in one place (T2.3).
 *
 * WHY THIS IS ITS OWN HOOK. It was the first ~110 lines of `useSocialMedia`,
 * where it was welded to that hook's chart fetching. The product dashboards
 * need the state without the fetching — they read their charts through the
 * small `socialdash` hooks — and `useSocialMedia` is scheduled for deletion in
 * T6.3 "only after Track 2 has moved everything worth keeping out of it". This
 * is that move: the state is the part worth keeping.
 *
 * It performs NO fetching and knows NO endpoint. What it produces is a
 * `labelQuery` that callers put on the wire, which is what lets the same
 * filters drive five small hooks and one large one without either side knowing
 * about the other.
 *
 * ⚠ THE STORED CONTROLS ARE PAIRED WITH THE KEY THEY CAME FROM. The dashboard
 * is not remounted when the scope changes, so without the render-phase
 * reconciliation below the next scope would inherit this one's platform subset
 * — often landing on the "no platforms selected" dead end — and the next toggle
 * would write it back under the new key. Reset in render, not in an effect:
 * there is nothing to clean up, and an effect would let one render fetch with
 * the wrong filter.
 */
export function useSocialFilters(options: UseSocialFiltersOptions = {}): UseSocialFiltersResult {
  const {
    customer = '',
    job = '',
    product,
    availablePlatforms = [],
    sourceSelectable = true,
    defaultDateRange = null,
  } = options

  const [selectedLabels, setSelectedLabels] = useState<Record<string, string[]>>({})
  const [rangeFilters, setRangeFilters] = useState<Record<string, RangeFilter>>({})

  // Platform + author controls. Both are restored from localStorage and written
  // back on every change, so a reload keeps the view the user built.
  const storage = typeof window === 'undefined' ? undefined : window.localStorage
  const [controlsState, setControlsState] = useState(() =>
    initialControlsState(storage, customer, job, product),
  )
  const resolvedControlsState = resolveControlsForJob(controlsState, storage, customer, job, product)
  if (resolvedControlsState !== controlsState) {
    setControlsState(resolvedControlsState)
  }
  const controls = resolvedControlsState.controls
  const { platformSelection } = controls

  // 🔴 THE INGEST PIN IS FIXED, NEVER CHOSEN (21 Oct 2026). A department's
  // server replaces it on every request (forceSourceScope), so the client sends
  // none. The selectable `all` view sends the same pin itself: Sky's Sprinklr
  // comments and the influencer scrapes, never the legacy Apify archive. The
  // old Sprinklr/Apify control and its `?source=` unlock are retired; a value a
  // user persisted under the old control is not read.
  const sourceScope: SourceScope = !sourceSelectable ? ALL_SOURCES : DASHBOARD_INGEST_SCOPE

  // The Source row — the post's BUSINESS source. Single-select like Platform;
  // not persisted (the row's default, All, is what a fresh page should show).
  const [sourceChoice, setSourceChoice] = useState<string>(ALL_SOURCES_CHOICE)
  const sources = useMemo(() => sourcesForQuery(sourceChoice), [sourceChoice])
  const selectSource = useCallback((choice: string) => setSourceChoice(choice || ALL_SOURCES_CHOICE), [])
  const clearSources = useCallback(() => setSourceChoice(ALL_SOURCES_CHOICE), [])

  // 🔴 THE AUTHOR SCOPE IS FORCED, AND THE PERSISTED VALUE IS DELIBERATELY
  // IGNORED. The control was removed from the toolbar (Kai, 2026-08-14: "that's
  // not a filter that makes sense"), and a filter with no visible control is
  // exactly the invisible default the toolbar exists to prevent.
  //
  // Reading `controls.authorScope` here would be worse than not removing the
  // control at all: anyone whose last visit persisted `audience` would keep
  // silently excluding brand replies forever, with nothing on screen saying so
  // and no way to change it. The stored key is left alone rather than migrated
  // — nothing reads it, and rewriting every user's localStorage to erase a
  // setting is a bigger action than ignoring it.
  //
  // ⚠ This WIDENED every chart: brand replies were excluded and now are not.
  const authorScope: AuthorScope = 'all'

  const persistControls = useCallback(
    (next: {
      platformSelection: PlatformSelection
      authorScope: AuthorScope
      sourceScope: SourceScope
    }) => {
      setControlsState({ key: dashboardControlsKey(customer, job, product), controls: next })
      saveDashboardControls(storage, customer, job, next, product)
    },
    [storage, customer, job, product],
  )

  const setPlatformSelection = useCallback(
    (selection: PlatformSelection) => persistControls({ ...controls, platformSelection: selection }),
    [persistControls, controls],
  )

  const setAuthorScope = useCallback(
    (scope: AuthorScope) => persistControls({ ...controls, authorScope: scope }),
    [persistControls, controls],
  )

  // One click, one platform (or All) — the control is single-select, so there
  // is no "current selection plus this one" to compute and `availablePlatforms`
  // is not consulted: an unknown choice cannot be clicked, and a stale stored
  // one is reconciled by normalizePlatformSelection on the way to the query.
  const selectPlatform = useCallback(
    (choice: PlatformChoice) => setPlatformSelection(choosePlatform(choice)),
    [setPlatformSelection],
  )

  // The wire value: [] means "no platform constraint at all", which is what
  // "all platforms" MUST send (a full list would still exclude parentless
  // records — see platformFilter.ts).
  const platforms = useMemo(
    () => platformsForQuery(platformSelection, availablePlatforms),
    [platformSelection, availablePlatforms],
  )

  // The default is read on mount only; a later prop change does not move a
  // range the user may already have edited.
  const defaultDateRangeRef = useRef(defaultDateRange)
  const [publishedDateRange, setPublishedDateRange] = useState<DateRange | null>(defaultDateRangeRef.current)
  const [commentDateRange, setCommentDateRange] = useState<DateRange | null>(defaultDateRangeRef.current)
  const resetDateRanges = useCallback(() => {
    setPublishedDateRange(defaultDateRangeRef.current)
    setCommentDateRange(defaultDateRangeRef.current)
  }, [])
  const [metadataFilters, setMetadataFilters] = useState<Record<string, string>>({})
  // Sprinklr campaign ids. Empty = no constraint, matching every other filter
  // here — a full list would NOT be equivalent, because it would still exclude
  // any entity whose campaign we never learned.
  const [campaigns, setCampaigns] = useState<string[]>([])

  // The flat filter state: entity-level dimensions (labels, platforms, metric
  // ranges, published date) + record-level ones (metadata, comment date).
  const filterState = useMemo(
    (): SocialFilterState => ({
      ...EMPTY_SOCIAL_FILTER_STATE,
      labelFilters: selectedLabels,
      platforms,
      sources,
      campaigns,
      metricRanges: rangeFilters,
      publishedDateRange,
      metadataFilters,
      commentDateRange,
      authorScope,
      sourceScope,
    }),
    [
      selectedLabels,
      platforms,
      sources,
      campaigns,
      rangeFilters,
      publishedDateRange,
      metadataFilters,
      commentDateRange,
      authorScope,
      sourceScope,
    ],
  )

  const labelQuery = useMemo((): LabelQuery => buildLabelQuery(filterState), [filterState])
  const labelsKey = useMemo(() => JSON.stringify(labelQuery), [labelQuery])
  const comparisonInfo = useMemo(() => detectComparisonMode(filterState), [filterState])

  const clearLabels = useCallback(() => setSelectedLabels({}), [])
  const clearRangeFilters = useCallback(() => setRangeFilters({}), [])
  // Back to the unconstrained "all platforms" state, which is NOT the same as
  // ticking every platform.
  const clearPlatforms = useCallback(() => setPlatformSelection(ALL_PLATFORMS), [setPlatformSelection])
  const clearCampaigns = useCallback(() => setCampaigns([]), [])
  const clearMetadataFilters = useCallback(() => setMetadataFilters({}), [])

  return {
    selectedLabels,
    setSelectedLabels,
    clearLabels,
    rangeFilters,
    setRangeFilters,
    clearRangeFilters,
    platforms,
    clearPlatforms,
    platformSelection,
    setPlatformSelection,
    platformChoice: platformChoice(platformSelection),
    selectPlatform,
    campaigns,
    setCampaigns,
    clearCampaigns,
    authorScope,
    setAuthorScope,
    sourceScope,
    sourceChoice,
    selectSource,
    clearSources,
    publishedDateRange,
    setPublishedDateRange,
    commentDateRange,
    setCommentDateRange,
    resetDateRanges,
    metadataFilters,
    setMetadataFilters,
    clearMetadataFilters,
    filterState,
    labelQuery,
    labelsKey,
    comparisonInfo,
  }
}

export default useSocialFilters
